import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import {withStyles, MuiThemeProvider} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import {emphasize} from '@material-ui/core/styles/colorManipulator';
import _ from '../../../utils/ObjectUtils';
import {fetchTheme} from "../../../style/MUIThemes";


const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    input: {
        display: 'flex',
        padding: 0
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden',
    },
    chip: {
        margin: `${theme.spacing(0.5)}px ${theme.spacing(0.25)}px`,
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08,
        ),
    },
    noOptionsMessage: {
        padding: `${theme.spacing()}px ${theme.spacing(2)}px`,
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: "16px !important",
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing(),
        left: 0,
        right: 0,
        fontFamily: "Rajdhani, Arial, Helvetica, sans-serif",
    },
    menuItem: {
        fontFamily: "Rajdhani, Arial, Helvetica, sans-serif",
    },
    divider: {
        height: theme.spacing(2)
    },
});

function NoOptionsMessage(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }) {
    return <div ref={inputRef} {...props} />;
}

function Control(props) {
    return (
        <TextField
            fullWidth
            InputProps={{
                inputComponent,
                inputRef: props.innerRef,
                inputProps: {
                    className: props.selectProps.classes.input,
                    children: props.children,
                    ...props.innerProps
                }
            }}
            {...props.selectProps.textFieldProps}
        />
    );
}

function Option(props) {
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            className={props.selectProps.classes.menuItem}
            component="div"
            style={{
                fontWeight: props.isSelected ? 500 : 400,
            }}
            {...props.innerProps}
        >
            {props.children}
        </MenuItem>
    );
}

function Placeholder(props) {
    return (
        <Typography
            className={props.selectProps.classes.placeholder}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

function SingleValue(props) {
    return (
        <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function ValueContainer(props) {
    return (
        <div
            className={props.selectProps.classes.valueContainer}
        >
            {props.children}
        </div>
    );
}

function MultiValue(props) {
    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={classNames(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused,
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...props.removeProps} />}
        />
    );
}

function Menu(props) {
    return (
        <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
            {props.children}
        </Paper>
    );
}

function IndicatorsContainer(props) {
    return !props.isDisabled && (
        <div
            style={{
                display: "inline-flex",
                cursor: "initial"
            }}
        >
            {props.children}
        </div>
    );
}

const components = {
    Control,
    Placeholder,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    SingleValue,
    ValueContainer,
    IndicatorsContainer
};

const IntegrationReactSelect =
    ({
         classes,
         theme,
         options,
         handleChange,
         singleValue,
         multiValue,
         placeholder,
         label,
         isClearable,
         isMulti,
         error,
         disabled,
         themeName
     }) => {
        const selectStyles = {
            input: base => ({
                ...base,
                color: themeName === "white" ? "#ffffff" : theme.palette.text.primary,
                '& input': {
                    font: 'inherit',
                },
            }),
        };

        const value = isMulti ?
            _.isUndefined(multiValue) ? null : multiValue :
            _.isUndefined(singleValue) ? null : singleValue;

        const componentsToReplace = components;
        /*if(options.length > 100) {
            componentsToReplace.MenuList = MenuForBigLists;
        }*/

        return (
            <div className={classes.root}>
                <NoSsr>
                    <MuiThemeProvider theme={fetchTheme(themeName)} >
                        <Select
                            classes={classes}
                            styles={selectStyles}
                            textFieldProps={{
                                label,
                                InputLabelProps: {
                                    shrink: true,
                                },
                                disabled: disabled,
                                error: !_.isNilOrEmpty(error),
                                helperText: error
                            }}
                            isDisabled={disabled}
                            options={options}
                            components={componentsToReplace}
                            value={value}
                            onChange={handleChange}
                            placeholder={placeholder}
                            isMulti={isMulti}
                            isClearable={isClearable}
                        />
                    </MuiThemeProvider>
                </NoSsr>
            </div>
        );
    };

IntegrationReactSelect.propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(IntegrationReactSelect);
export {
    components,
    styles
};